import { useCallback, useEffect, useRef, useState } from 'react';
import { parseGuestReply } from '@rally/core';
import type { ParsedReply } from '@rally/core';
import { api } from '../api.ts';
import type { GuestView } from '../api.ts';
import { loadListener, recordUtterance } from '../components/neuralSpeech.ts';

/**
 * What an invited friend sees: a private link, no app, no account. They
 * answer, pick the dates they can make, and (only if they want) share their
 * own needs, which Rally uses to pick places and nobody else can see.
 */

const DIETS: Array<[string, string]> = [
  ['vegetarian', 'Vegetarian'],
  ['vegan', 'Vegan'],
  ['gluten_free', 'Gluten-free'],
  ['dairy_free', 'Dairy-free'],
  ['halal', 'Halal'],
  ['kosher', 'Kosher'],
];
const ALLERGIES: Array<[string[], string]> = [
  [['tree_nuts', 'peanuts'], 'Nuts'],
  [['shellfish'], 'Shellfish'],
  [['fish'], 'Fish'],
  [['milk'], 'Dairy'],
  [['eggs'], 'Eggs'],
  [['wheat'], 'Wheat'],
  [['soy'], 'Soy'],
  [['sesame'], 'Sesame'],
];

export function Guest({ token }: { token: string }) {
  const [view, setView] = useState<GuestView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canMake, setCanMake] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [needs, setNeeds] = useState<GuestView['you']['needs'] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [paying, setPaying] = useState<'idle' | 'sheet' | 'paying'>('idle');
  const [heard, setHeard] = useState<{ phase: 'idle' | 'loading' | 'listening' | 'thinking' | 'heard' | 'error'; message?: string; text?: string; reply?: ParsedReply }>({ phase: 'idle' });
  const recording = useRef<{ stop: () => void } | undefined>(undefined);

  /** Answer by voice: recorded and transcribed on this phone, then shown for a tap to send. */
  async function sayAnswer() {
    if (!view) return;
    if (heard.phase === 'listening') return recording.current?.stop();
    try {
      setHeard({ phase: 'loading', message: 'Getting the on-device listener ready…' });
      const listen = await loadListener((message) => setHeard({ phase: 'loading', message }));
      setHeard({ phase: 'listening', message: 'Listening… try “Saturday works, not Sunday”.' });
      const rec = recordUtterance();
      recording.current = rec;
      const audio = await rec.done;
      setHeard({ phase: 'thinking', message: 'Understanding on your phone…' });
      const text = await listen(audio);
      const reply = parseGuestReply(text, view.options.map((o) => ({ id: o.id, start: o.start, end: o.start, label: o.label })), view.timezone);
      if (reply?.canMake.length) setCanMake(reply.canMake);
      setHeard({ phase: 'heard', text, reply });
    } catch (err) {
      const denied = (err as Error).name === 'NotAllowedError';
      setHeard({ phase: 'error', message: denied ? 'Rally needs the microphone for that. You can tap your answer instead.' : "Voice isn't available on this phone. You can tap your answer instead." });
    }
  }

  const load = useCallback(async () => {
    try {
      const v = await api.guest(token);
      setView(v);
      setCanMake((cur) => (cur.length || v.you.status === 'sent' ? cur : v.you.canMake.length ? v.you.canMake : v.options.map((o) => o.id)));
      setNeeds((cur) => cur ?? v.you.needs);
      setNote((cur) => cur || v.you.note || '');
    } catch (err) {
      setError((err as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <div className="phone-page"><div className="phone-card"><h1>Hmm.</h1><p>{error}</p></div></div>;
  if (!view || !needs) return <div className="phone-page"><div className="phone-card">Loading your invite…</div></div>;

  const answered = view.you.status === 'in' || view.you.status === 'out' || view.you.status === 'maybe';
  const toggle = (id: string) => setCanMake((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function answer(status: 'in' | 'out' | 'maybe') {
    setSaving(true);
    try {
      const v = await api.answer(token, { status, canMake: status === 'out' ? [] : canMake, note: note || undefined });
      setView(v);
      setSaved(status === 'out' ? "Got it. We'll miss you!" : 'Thanks! Rally has your answer.');
    } finally {
      setSaving(false);
    }
  }

  async function saveNeeds() {
    setSaving(true);
    try {
      const v = await api.needs(token, { diets: needs!.diets, allergies: needs!.allergies, stepFree: needs!.stepFree, budgetPerHeadCents: needs!.budgetPerHeadCents });
      setView(v);
      setSaved('Saved. Rally will only suggest places that work for you.');
    } finally {
      setSaving(false);
    }
  }

  async function pay() {
    if (!view?.you.depositItemId) return;
    setPaying('paying');
    const r = await api.payDeposit(view.you.depositItemId, view.you.name, token);
    setPaying('idle');
    setSaved(r.status === 'completed' ? `Deposit paid to ${view.venue?.name}.` : r.error ?? 'Payment failed');
    await load();
  }

  const diet = (id: string) => needs.diets.includes(id);
  const allergy = (ids: string[]) => ids.every((a) => needs.allergies.includes(a));

  return (
    <div className="phone-page">
      <div className="phone-card">
        <div className="phone-brand">Rally</div>
        <p className="phone-kicker">{view.organizer} invited you</p>
        <h1>{view.title}</h1>
        {view.surpriseFor.length > 0 && <div className="shh">🤫 It’s a surprise for {view.surpriseFor.join(' and ')}. Please keep it quiet!</div>}

        {view.venue ? (
          <div className="booked">
            <div className="booked-k">It’s set</div>
            <h2>{view.venue.name}</h2>
            <p>
              {view.venue.when}
              <br />
              {view.venue.address}
              <br />
              {view.venue.room === 'private' ? 'Private room' : 'Table'} · confirmation {view.venue.code}
            </p>
            <div className="row">
              <a className="btn" href={`/api/guest/${token}/calendar.ics`}>
                Add to calendar
              </a>
            </div>
            {view.you.deposit?.status === 'due' && (
              <div className="deposit">
                <p>
                  The private room needs a ${(view.you.deposit.amountCents / 100).toFixed(0)} deposit, paid straight to {view.venue.name}.
                </p>
                {paying === 'sheet' ? (
                  <div className="paysheet">
                    <div>
                      <b>Amazon Pay</b> (simulated) · Card •••• 1881
                    </div>
                    <button onClick={() => void pay()}>Pay ${(view.you.deposit.amountCents / 100).toFixed(2)}</button>
                    <button className="ghost" onClick={() => setPaying('idle')}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setPaying('sheet')} disabled={paying === 'paying'}>
                    {paying === 'paying' ? 'Paying…' : 'Pay deposit with Amazon Pay'}
                  </button>
                )}
              </div>
            )}
            {view.you.deposit?.status === 'paid' && <div className="ok">✓ Deposit paid</div>}
          </div>
        ) : (
          <>
            <div className="voice-answer">
              <button className={`ghost ${heard.phase === 'listening' ? 'on' : ''}`} onClick={() => void sayAnswer()} disabled={heard.phase === 'loading' || heard.phase === 'thinking'}>
                {heard.phase === 'listening' ? '● Done' : '🎙 Say your answer'}
              </button>
              {heard.message && heard.phase !== 'heard' && <span className="muted">{heard.message}</span>}
              {heard.phase === 'heard' && (
                <div className="heard">
                  <div>
                    I heard “{heard.text?.replace(/[.!?]+$/, '') || '…'}”.{' '}
                    {heard.reply ? <b>{heard.reply.understood}.</b> : "I didn't catch a day; tap your answer below."}
                  </div>
                  {heard.reply && (
                    <button disabled={saving} onClick={() => void answer(heard.reply!.status)}>
                      Send: {heard.reply.status === 'out' ? 'can’t make it' : heard.reply.status === 'maybe' ? 'maybe' : 'I’m in'}
                    </button>
                  )}
                </div>
              )}
              <span className="fine">Your voice is turned into text on this phone and never uploaded.</span>
            </div>
            <h3>Which can you make?</h3>
            <div className="dates">
              {view.options.map((o) => (
                <button key={o.id} className={`date ${canMake.includes(o.id) ? 'on' : ''}`} onClick={() => toggle(o.id)} aria-pressed={canMake.includes(o.id)}>
                  {o.label}
                </button>
              ))}
            </div>
            <input className="note" placeholder="Add a note (optional)" value={note} maxLength={280} onChange={(e) => setNote(e.target.value)} />
            <div className="answers">
              <button disabled={saving || canMake.length === 0} onClick={() => void answer('in')} className={view.you.status === 'in' ? 'picked' : ''}>
                I’m in
              </button>
              <button className={`ghost ${view.you.status === 'maybe' ? 'picked' : ''}`} disabled={saving || canMake.length === 0} onClick={() => void answer('maybe')}>
                Maybe
              </button>
              <button className={`ghost ${view.you.status === 'out' ? 'picked' : ''}`} disabled={saving} onClick={() => void answer('out')}>
                Can’t make it
              </button>
            </div>
          </>
        )}
        {saved && <div className="ok">{saved}</div>}
        {view.coming.length > 0 && <p className="coming">Coming: {view.coming.join(', ')}</p>}

        <details open={!answered}>
          <summary>Anything Rally should know about you?</summary>
          <p className="fine">Only used to pick places that work for you. Only you can see or change this; {view.organizer} sees the group’s needs as counts, never yours by name.</p>
          <div className="chips">
            {DIETS.map(([id, label]) => (
              <button key={id} className={`chip ${diet(id) ? 'on' : ''}`} onClick={() => setNeeds({ ...needs, diets: diet(id) ? needs.diets.filter((d) => d !== id) : [...needs.diets, id] })}>
                {label}
              </button>
            ))}
          </div>
          <div className="chips">
            {ALLERGIES.map(([ids, label]) => (
              <button
                key={label}
                className={`chip ${allergy(ids) ? 'on' : ''}`}
                onClick={() => setNeeds({ ...needs, allergies: allergy(ids) ? needs.allergies.filter((a) => !ids.includes(a)) : [...new Set([...needs.allergies, ...ids])] })}
              >
                {label} allergy
              </button>
            ))}
          </div>
          <label className="check">
            <input type="checkbox" checked={needs.stepFree} onChange={(e) => setNeeds({ ...needs, stepFree: e.target.checked })} /> I need step-free access
          </label>
          <label className="check">
            Budget per person
            <select value={needs.budgetPerHeadCents ?? ''} onChange={(e) => setNeeds({ ...needs, budgetPerHeadCents: e.target.value ? Number(e.target.value) : null })}>
              <option value="">No limit</option>
              <option value="2500">Under $25</option>
              <option value="4000">Under $40</option>
              <option value="6000">Under $60</option>
            </select>
          </label>
          <button className="ghost" disabled={saving} onClick={() => void saveNeeds()}>
            Save my needs
          </button>
        </details>
        <p className="fine">Sent with Rally on Alexa+. No app or account needed.</p>
      </div>
    </div>
  );
}
